import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../ui-components/ui/card';
import { Badge } from '../../ui-components/ui/badge';
import { Button } from '../../ui-components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui-components/ui/tabs';
import { 
  Clock, 
  Phone, 
  Mail, 
  Calendar, 
  CheckCircle2, 
  AlertCircle,
  Timer,
  User,
  Building2
} from 'lucide-react';
import { format, formatDistanceToNow, isBefore, isToday, addMinutes } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAppointments } from '../../lib/hooks/useAppointments';
import { AppointmentStatus, AppointmentType } from '../../lib/interfaces/appointment.interface';
import { toast } from 'sonner';
import useApiRequest from '../../lib/hooks/use-request';
import { useNavigate } from 'react-router-dom';
import { Paginated } from '../../lib/interfaces/utils';

interface Task {
  id: string;
  type: 'appointment' | 'auto_task';
  title: string;
  description?: string;
  dueDate: Date;
  dealId: string;
  dealTitle: string;
  contactName?: string;
  organizationName?: string;
  priority: 'high' | 'medium' | 'low';
  appointmentType?: AppointmentType;
  autoTaskType?: '20min_email' | 'second_call';
}

const TaskBoard: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overdue' | 'today' | 'upcoming'>('today');
  const { appointments, markAsComplete } = useAppointments();
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();

  useEffect(() => {
    // Small delay to ensure appointments are loaded
    const timer = setTimeout(() => {
      loadTasks();
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    const allTasks: Task[] = [];

    // Convert appointments to tasks
    appointments
      .filter(apt => apt.status === AppointmentStatus.SCHEDULED)
      .forEach(apt => {
        allTasks.push({
          id: apt._id,
          type: 'appointment',
          title: apt.title,
          description: apt.description,
          dueDate: new Date(apt.start),
          dealId: apt.dealId,
          dealTitle: apt.dealName || 'Deal',
          contactName: apt.contactName,
          organizationName: undefined, // Not available in current appointment interface
          priority: isBefore(new Date(apt.start), new Date()) ? 'high' : 
                   isToday(new Date(apt.start)) ? 'medium' : 'low',
          appointmentType: apt.type,
        });
      });

    // TODO: Re-enable auto-tasks when API is properly configured
    // Currently disabled to prevent error modal on page load
    /*
    try {
      const response = await apiRequest<Paginated<any>>('deals', 'GET');
      
      // Only process if we got a successful response
      if (response.data && response.data.docs && !response.error) {
        response.data.docs.forEach((deal: any) => {
          // Check if deal needs a 20-minute email follow-up
          if (deal.lastContactAttempt && !deal.firstEmailSent) {
            const attemptTime = new Date(deal.lastContactAttempt);
            const emailDue = addMinutes(attemptTime, 20);
            
            if (isBefore(emailDue, new Date())) {
              allTasks.push({
                id: `email-${deal._id}`,
                type: 'auto_task',
                title: '20-Min E-Mail senden',
                description: 'Nicht erreicht - Follow-up E-Mail senden',
                dueDate: emailDue,
                dealId: deal._id,
                dealTitle: deal.title,
                contactName: deal.personPreview?.fullName,
                organizationName: deal.organizationPreview?.name,
                priority: 'high',
                autoTaskType: '20min_email',
              });
            }
          }

          // Check if deal needs a second call attempt
          if (deal.firstCallAttempt && !deal.secondCallAttempt && deal.callAttemptCount < 2) {
            const firstCallTime = new Date(deal.firstCallAttempt);
            const secondCallDue = addMinutes(firstCallTime, 90); // 1.5 hours later
            
            if (isBefore(secondCallDue, new Date())) {
              allTasks.push({
                id: `call2-${deal._id}`,
                type: 'auto_task',
                title: '2. Anrufversuch',
                description: 'Zweiter Kontaktversuch erforderlich',
                dueDate: secondCallDue,
                dealId: deal._id,
                dealTitle: deal.title,
                contactName: deal.personPreview?.fullName,
                organizationName: deal.organizationPreview?.name,
                priority: 'high',
                autoTaskType: 'second_call',
              });
            }
          }
        });
      }
    } catch (error) {
      console.warn('Could not load deals for auto-tasks:', error);
      // Don't show error modal for this, just continue without auto-tasks
    }
    */

    setTasks(allTasks.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime()));
    setLoading(false);
  }, [appointments]);

  const handleTaskComplete = async (task: Task) => {
    if (task.type === 'appointment') {
      await markAsComplete(task.id, 'Aufgabe erledigt');
      toast.success('Aufgabe als erledigt markiert');
    } else {
      // Mark auto-task as complete by updating deal
      await apiRequest(`deals/${task.dealId}`, 'PATCH', {
        body: {
          [task.autoTaskType === '20min_email' ? 'firstEmailSent' : 'secondCallAttempt']: new Date(),
        }
      });
      toast.success('Automatische Aufgabe erledigt');
    }
    loadTasks();
  };

  const getTaskIcon = (task: Task) => {
    if (task.type === 'appointment') {
      switch (task.appointmentType) {
        case AppointmentType.CALL:
          return <Phone className="h-4 w-4" />;
        case AppointmentType.MEETING:
        case AppointmentType.SITE_VISIT:
          return <Calendar className="h-4 w-4" />;
        case AppointmentType.EMAIL:
          return <Mail className="h-4 w-4" />;
        default:
          return <Clock className="h-4 w-4" />;
      }
    } else {
      return task.autoTaskType === '20min_email' ? 
        <Mail className="h-4 w-4" /> : 
        <Phone className="h-4 w-4" />;
    }
  };

  const filterTasks = (tab: 'overdue' | 'today' | 'upcoming') => {
    const now = new Date();
    return tasks.filter(task => {
      if (tab === 'overdue') {
        return isBefore(task.dueDate, now) && !isToday(task.dueDate);
      } else if (tab === 'today') {
        return isToday(task.dueDate);
      } else {
        return !isBefore(task.dueDate, now) && !isToday(task.dueDate);
      }
    });
  };

  const TaskCard: React.FC<{ task: Task }> = ({ task }) => (
    <Card className="hover:shadow-md transition-shadow cursor-pointer">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {getTaskIcon(task)}
              <h4 className="font-medium">{task.title}</h4>
              {task.type === 'auto_task' && (
                <Badge variant="secondary" className="text-xs">
                  <Timer className="h-3 w-3 mr-1" />
                  Auto
                </Badge>
              )}
            </div>
            
            {task.description && (
              <p className="text-sm text-gray-600 mb-2">{task.description}</p>
            )}
            
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span 
                className="hover:underline cursor-pointer"
                onClick={() => navigate(`/deals/${task.dealId}`)}
              >
                {task.dealTitle}
              </span>
              
              {task.contactName && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {task.contactName}
                </span>
              )}
              
              {task.organizationName && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {task.organizationName}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2 mt-2">
              <Badge 
                variant={task.priority === 'high' ? 'destructive' : 
                        task.priority === 'medium' ? 'default' : 'secondary'}
                className="text-xs"
              >
                {task.priority === 'high' && <AlertCircle className="h-3 w-3 mr-1" />}
                {formatDistanceToNow(task.dueDate, { locale: de, addSuffix: true })}
              </Badge>
              
              <span className="text-xs text-gray-500">
                {format(task.dueDate, 'HH:mm', { locale: de })}
              </span>
            </div>
          </div>
          
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleTaskComplete(task)}
            className="ml-2"
          >
            <CheckCircle2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <div className="p-8 text-center">Lade Aufgaben...</div>;
  }

  const overdueTasks = filterTasks('overdue');
  const todayTasks = filterTasks('today');
  const upcomingTasks = filterTasks('upcoming');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Aufgaben-Board</h1>
        <p className="text-gray-600">Ihre fälligen Aufgaben und automatische Erinnerungen</p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overdue" className="relative">
            Überfällig
            {overdueTasks.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1">
                {overdueTasks.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="today" className="relative">
            Heute
            {todayTasks.length > 0 && (
              <Badge className="ml-2 h-5 px-1">
                {todayTasks.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="upcoming">
            Anstehend
            {upcomingTasks.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 px-1">
                {upcomingTasks.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overdue" className="mt-6">
          {overdueTasks.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <p className="text-gray-600">Keine überfälligen Aufgaben!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {overdueTasks.map(task => (
                <TaskCard key={task.id} task={task} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="today" className="mt-6">
          {todayTasks.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Keine Aufgaben für heute</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {todayTasks.map(task => (
                <TaskCard key={task.id} task={task} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="upcoming" className="mt-6">
          {upcomingTasks.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Keine anstehenden Aufgaben</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {upcomingTasks.map(task => (
                <TaskCard key={task.id} task={task} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TaskBoard;
